import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getApiUrl } from '../../utils/api';
import { formatBytes, formatDate } from '../../utils/formatters';

// --- Types ---

interface StorageItem {
  name: string;
  path: string;
  type: 'directory' | 'file';
  size?: number;
  last_modified?: string;
  etag?: string;
  s3_key?: string;
  bucket?: string;
}

interface AdminSampleExplorerProps {
  onSyncNeeded?: () => void;
}

// --- Helpers ---

const getToken = () => localStorage.getItem('token') || localStorage.getItem('authToken');

const API_BASE = '/api/v1/admin/samples';

const sortItems = (items: StorageItem[]): StorageItem[] => {
  return [...items].sort((a, b) => {
    if (a.type === 'directory' && b.type !== 'directory') return -1;
    if (a.type !== 'directory' && b.type === 'directory') return 1;
    return a.name.localeCompare(b.name);
  });
};

/**
 * Strip the `samples/` prefix so paths sent to the API are relative.
 * The backend will re-prepend it.
 */
const toRelativePath = (fullPath: string) => {
  if (fullPath.startsWith('samples/')) return fullPath.slice('samples/'.length);
  if (fullPath === 'samples') return '';
  return fullPath;
};

// --- Icons ---

const FolderClosedIcon = () => (
  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
  </svg>
);

const FolderOpenIcon = () => (
  <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1H8a3 3 0 00-3 3v1.5a1.5 1.5 0 01-3 0V6z" clipRule="evenodd" />
    <path d="M6 12a2 2 0 012-2h8a2 2 0 012 2v2a2 2 0 01-2 2H2h2a2 2 0 002-2v-2z" />
  </svg>
);

const DirChevron = ({ isExpanded, isLoading }: { isExpanded: boolean; isLoading: boolean }) => {
  if (isLoading) {
    return (
      <svg className="w-3 h-3 animate-spin text-gray-500 mr-1 flex-shrink-0" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
        <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    );
  }
  return (
    <svg
      className={`w-3 h-3 text-gray-500 mr-1 flex-shrink-0 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
};

const getFileIcon = (name: string) => {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (ext === 'ipynb') {
    return (
      <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  if (['py', 'js', 'ts', 'jsx', 'tsx'].includes(ext)) {
    return (
      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    );
  }
  if (['csv', 'json', 'parquet', 'xlsx', 'xls'].includes(ext)) {
    return (
      <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
};

// --- Component ---

const AdminSampleExplorer: React.FC<AdminSampleExplorerProps> = ({ onSyncNeeded }) => {
  // Navigation (paths are full S3 keys like "samples/getting-started")
  const [currentPath, setCurrentPath] = useState('samples');
  const [pathHistory, setPathHistory] = useState<string[]>([]);

  // Items
  const [items, setItems] = useState<StorageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [lastClickedIndex, setLastClickedIndex] = useState<number>(-1);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: StorageItem } | null>(null);

  // Inline rename
  const [renamingFile, setRenamingFile] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Delete confirm
  const [deleteConfirmFile, setDeleteConfirmFile] = useState<string | null>(null);

  // New folder dialog
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Tree expansion
  const [expandedDirs, setExpandedDirs] = useState<Record<string, StorageItem[]>>({});
  const [loadingDirs, setLoadingDirs] = useState<Set<string>>(new Set());

  // Drag-and-drop
  const [draggedItem, setDraggedItem] = useState<StorageItem | null>(null);
  const [dropTargetDir, setDropTargetDir] = useState<string | null>(null);
  const [containerDropActive, setContainerDropActive] = useState(false);

  // --- Data fetching ---

  const fetchItems = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const relPath = toRelativePath(path);
      const params = new URLSearchParams();
      if (relPath) params.append('path', relPath);

      const response = await fetch(`${getApiUrl()}${API_BASE}/browse?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.status === 403) throw new Error('Admin access required');
      if (!response.ok) throw new Error('Failed to fetch files');

      const data = await response.json();

      const dirs: StorageItem[] = (data.directories || []).map((d: any) => ({
        ...d, type: 'directory' as const,
      }));
      const files: StorageItem[] = (data.files || []).map((f: any) => ({
        ...f, type: 'file' as const,
      }));

      setItems(sortItems([...dirs, ...files]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems(currentPath);
  }, [currentPath, fetchItems]);

  const handleRefresh = useCallback(() => {
    setExpandedDirs({});
    setLoadingDirs(new Set());
    fetchItems(currentPath);
  }, [currentPath, fetchItems]);

  // Fetch children for inline expand
  const fetchDirChildren = useCallback(async (dirPath: string): Promise<StorageItem[]> => {
    const token = getToken();
    if (!token) return [];

    const relPath = toRelativePath(dirPath);
    const params = new URLSearchParams();
    if (relPath) params.append('path', relPath);

    const response = await fetch(`${getApiUrl()}${API_BASE}/browse?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!response.ok) return [];

    const data = await response.json();
    const dirs: StorageItem[] = (data.directories || []).map((d: any) => ({
      ...d, type: 'directory' as const,
    }));
    const files: StorageItem[] = (data.files || []).map((f: any) => ({
      ...f, type: 'file' as const,
    }));
    return sortItems([...dirs, ...files]);
  }, []);

  // Toggle expand/collapse directory
  const toggleDir = useCallback(async (dirPath: string) => {
    if (expandedDirs[dirPath] !== undefined) {
      setExpandedDirs(prev => {
        const next = { ...prev };
        delete next[dirPath];
        for (const key of Object.keys(next)) {
          if (key.startsWith(dirPath + '/')) delete next[key];
        }
        return next;
      });
      return;
    }

    setLoadingDirs(prev => { const n = new Set(prev); n.add(dirPath); return n; });
    try {
      const children = await fetchDirChildren(dirPath);
      setExpandedDirs(prev => ({ ...prev, [dirPath]: children }));
    } catch {
      setError(`Failed to load ${dirPath}`);
    } finally {
      setLoadingDirs(prev => { const n = new Set(prev); n.delete(dirPath); return n; });
    }
  }, [expandedDirs, fetchDirChildren]);

  // --- Navigation ---

  const navigateTo = (path: string) => {
    if (path !== currentPath) {
      setPathHistory(prev => [...prev, currentPath]);
      setCurrentPath(path);
      setSelectedFiles(new Set());
      setLastClickedIndex(-1);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  const navigateBack = () => {
    if (pathHistory.length > 0) {
      const previous = pathHistory[pathHistory.length - 1];
      setPathHistory(prev => prev.slice(0, -1));
      setCurrentPath(previous);
      setSelectedFiles(new Set());
      setLastClickedIndex(-1);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  const navigateToRoot = () => {
    if (currentPath !== 'samples') {
      setPathHistory([]);
      setCurrentPath('samples');
      setSelectedFiles(new Set());
      setLastClickedIndex(-1);
      setExpandedDirs({});
      setLoadingDirs(new Set());
    }
  };

  // --- Selection ---

  const handleItemClick = (item: StorageItem, index: number, event: React.MouseEvent) => {
    if (event.ctrlKey || event.metaKey) {
      const newSelection = new Set(selectedFiles);
      if (newSelection.has(item.path)) {
        newSelection.delete(item.path);
      } else {
        newSelection.add(item.path);
      }
      setSelectedFiles(newSelection);
      setLastClickedIndex(index);
      return;
    }

    if (event.shiftKey && lastClickedIndex >= 0) {
      const start = Math.min(index, lastClickedIndex);
      const end = Math.max(index, lastClickedIndex);
      const newSelection = new Set<string>();
      for (let i = start; i <= end; i++) {
        newSelection.add(items[i].path);
      }
      setSelectedFiles(newSelection);
      return;
    }

    setSelectedFiles(new Set([item.path]));
    setLastClickedIndex(index);

    if (item.type === 'directory') {
      toggleDir(item.path);
    }
  };

  const handleItemDoubleClick = (item: StorageItem) => {
    if (item.type === 'directory') {
      navigateTo(item.path);
    }
  };

  // --- Upload ---

  const uploadFileToDir = useCallback(async (file: File, targetDir: string) => {
    const token = getToken();
    if (!token) throw new Error('No authentication token found');

    const relPath = toRelativePath(targetDir);
    const filePath = relPath ? `${relPath}/${file.name}` : file.name;

    // Get presigned URL
    const uploadUrlResponse = await fetch(`${getApiUrl()}${API_BASE}/upload-url`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        file_name: file.name,
        file_type: file.type || 'application/octet-stream',
        file_path: filePath,
        file_size: file.size,
      }),
    });

    if (!uploadUrlResponse.ok) throw new Error('Failed to get upload URL');

    const { url, tags } = await uploadUrlResponse.json();

    // Upload directly to S3
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        setUploadProgress((e.loaded / e.total) * 100);
      }
    });

    await new Promise((resolve, reject) => {
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      if (tags) {
        xhr.setRequestHeader('x-amz-tagging', tags);
      }
      xhr.onload = () => {
        if (xhr.status === 200 || xhr.status === 204) resolve(xhr.response);
        else reject(new Error(`Upload failed with status ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });

    // Notify backend that upload completed — triggers GCS replication
    await fetch(`${getApiUrl()}${API_BASE}/upload-complete?file_path=${encodeURIComponent(filePath)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
    }).catch(() => {}); // best-effort
  }, []);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      await uploadFileToDir(file, currentPath);
      onSyncNeeded?.();
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // --- Download ---

  const handleDownload = async (item: StorageItem) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(`${getApiUrl()}${API_BASE}/download-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ file_path: toRelativePath(item.path) }),
      });

      if (!response.ok) throw new Error('Failed to get download URL');
      const { url } = await response.json();
      window.open(url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    }
  };

  // --- Delete ---

  const handleDeleteConfirmed = async (itemsToDelete: StorageItem[]) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      for (const item of itemsToDelete) {
        await fetch(`${getApiUrl()}${API_BASE}/files`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ file_path: toRelativePath(item.path) }),
        });
      }

      setSelectedFiles(new Set());
      setDeleteConfirmFile(null);
      onSyncNeeded?.();
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // --- Rename ---

  const startRename = (item: StorageItem) => {
    setRenamingFile(item.path);
    setRenameValue(item.name);
    setContextMenu(null);
  };

  const handleRenameSubmit = async (item: StorageItem) => {
    if (!renameValue.trim() || renameValue === item.name) {
      setRenamingFile(null);
      return;
    }

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const dir = item.path.substring(0, item.path.lastIndexOf('/') + 1);
      const newPath = dir + renameValue.trim();

      const response = await fetch(`${getApiUrl()}${API_BASE}/rename`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          old_path: toRelativePath(item.path),
          new_path: toRelativePath(newPath),
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || `Rename failed (${response.status})`);
      }

      setRenamingFile(null);
      onSyncNeeded?.();
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rename failed');
      setRenamingFile(null);
    }
  };

  // --- Create folder ---

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const relPath = toRelativePath(currentPath);
      const folderPath = relPath
        ? `${relPath}/${newFolderName.trim()}`
        : newFolderName.trim();

      const response = await fetch(`${getApiUrl()}${API_BASE}/create-folder`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ folder_path: folderPath }),
      });

      if (!response.ok) throw new Error('Failed to create folder');

      setShowNewFolderDialog(false);
      setNewFolderName('');
      onSyncNeeded?.();
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  };

  // --- Copy path ---

  const handleCopyPath = (item: StorageItem) => {
    navigator.clipboard.writeText(`/home/propalgos/${item.path}`);
    setContextMenu(null);
  };

  // --- Drag and drop ---

  const handleDragStart = (e: React.DragEvent, item: StorageItem) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.path);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTargetDir(null);
  };

  const handleDragOver = (e: React.DragEvent, targetPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    // External OS file drop
    if (!draggedItem && e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
      setDropTargetDir(targetPath);
      return;
    }
    if (!draggedItem) return;
    if (draggedItem.path === targetPath || targetPath.startsWith(draggedItem.path + '/')) {
      e.dataTransfer.dropEffect = 'none';
      return;
    }
    e.dataTransfer.dropEffect = 'move';
    setDropTargetDir(targetPath);
  };

  const handleDragLeave = (e: React.DragEvent, targetPath: string) => {
    e.stopPropagation();
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    if (relatedTarget && (e.currentTarget as HTMLElement).contains(relatedTarget)) return;
    if (dropTargetDir === targetPath) setDropTargetDir(null);
  };

  const handleDrop = async (e: React.DragEvent, targetDirPath: string) => {
    e.preventDefault();
    e.stopPropagation();
    setDropTargetDir(null);

    // External OS file drop
    if (!draggedItem && e.dataTransfer.files.length > 0) {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      try {
        const files = Array.from(e.dataTransfer.files);
        for (const file of files) {
          await uploadFileToDir(file, targetDirPath);
        }
        onSyncNeeded?.();
        await handleRefresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Upload failed');
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
      return;
    }

    if (!draggedItem) return;
    if (draggedItem.path === targetDirPath || targetDirPath.startsWith(draggedItem.path + '/')) {
      setDraggedItem(null);
      return;
    }

    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const fileName = draggedItem.name;
      const newPath = `${targetDirPath}/${fileName}`;

      const oldRel = toRelativePath(draggedItem.path);
      const newRel = toRelativePath(newPath);

      const response = await fetch(`${getApiUrl()}${API_BASE}/rename`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          old_path: oldRel,
          new_path: newRel,
        }),
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        let detail = '';
        try { detail = JSON.parse(errText)?.detail; } catch { /* not json */ }
        throw new Error(detail || `Move failed (${response.status}): ${oldRel} → ${newRel} | ${errText.slice(0, 200)}`);
      }
      onSyncNeeded?.();
      await handleRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to move file');
    } finally {
      setDraggedItem(null);
    }
  };

  // --- Context menu close ---

  useEffect(() => {
    if (!contextMenu) return;
    const handleClose = () => setContextMenu(null);
    document.addEventListener('mousedown', handleClose);
    document.addEventListener('scroll', handleClose, true);
    return () => {
      document.removeEventListener('mousedown', handleClose);
      document.removeEventListener('scroll', handleClose, true);
    };
  }, [contextMenu]);

  // --- Focus rename input ---

  useEffect(() => {
    if (renamingFile && renameInputRef.current) {
      requestAnimationFrame(() => {
        if (!renameInputRef.current) return;
        renameInputRef.current.focus();
        const val = renameInputRef.current.value;
        const dotIndex = val.lastIndexOf('.');
        if (dotIndex > 0) {
          renameInputRef.current.setSelectionRange(0, dotIndex);
        } else {
          renameInputRef.current.select();
        }
      });
    }
  }, [renamingFile]);

  // --- Find item helper ---

  const findItem = useCallback((path: string): StorageItem | undefined => {
    const found = items.find(f => f.path === path);
    if (found) return found;
    for (const children of Object.values(expandedDirs)) {
      const child = children.find(f => f.path === path);
      if (child) return child;
    }
    return undefined;
  }, [items, expandedDirs]);

  // --- Render a single row ---

  const renderRow = (item: StorageItem, index: number, depth: number = 0) => {
    const isSelected = selectedFiles.has(item.path);
    const isExpanded = expandedDirs[item.path] !== undefined;
    const isDirLoading = loadingDirs.has(item.path);
    const isRenaming = renamingFile === item.path;
    const isDropTarget = dropTargetDir === item.path;
    const isDeleting = deleteConfirmFile === item.path;

    return (
      <React.Fragment key={item.path}>
        <div
          className={`group flex items-center py-1.5 px-3 cursor-pointer select-none text-sm border-b border-gray-800/50
            ${isSelected ? 'bg-blue-900/30 text-white' : 'text-gray-300 hover:bg-gray-800/50'}
            ${isDropTarget ? 'bg-blue-900/20 ring-1 ring-blue-500/50' : ''}
            ${isDeleting ? 'bg-red-900/20' : ''}
          `}
          style={{ paddingLeft: `${12 + depth * 20}px` }}
          onClick={(e) => handleItemClick(item, index, e)}
          onDoubleClick={() => handleItemDoubleClick(item)}
          onContextMenu={(e) => {
            e.preventDefault();
            setContextMenu({ x: e.clientX, y: e.clientY, item });
          }}
          draggable
          onDragStart={(e) => handleDragStart(e, item)}
          onDragEnd={handleDragEnd}
          onDragOver={item.type === 'directory' ? (e) => handleDragOver(e, item.path) : undefined}
          onDragLeave={item.type === 'directory' ? (e) => handleDragLeave(e, item.path) : undefined}
          onDrop={item.type === 'directory' ? (e) => handleDrop(e, item.path) : undefined}
        >
          {/* Chevron / icon */}
          {item.type === 'directory' ? (
            <>
              <DirChevron isExpanded={isExpanded} isLoading={isDirLoading} />
              {isExpanded ? <FolderOpenIcon /> : <FolderClosedIcon />}
            </>
          ) : (
            <span className="ml-4">{getFileIcon(item.name)}</span>
          )}

          {/* Name or rename input */}
          {isRenaming ? (
            <input
              ref={renameInputRef}
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => handleRenameSubmit(item)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit(item);
                if (e.key === 'Escape') setRenamingFile(null);
              }}
              className="ml-2 flex-1 bg-gray-800 text-white border border-blue-500 rounded px-2 py-0.5 text-sm outline-none"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="ml-2 truncate flex-1 text-left">{item.name}</span>
          )}

          {/* Size & date */}
          {item.type === 'file' && !isRenaming && (
            <div className="hidden sm:flex items-center gap-4 ml-auto text-xs text-gray-500">
              <span className="w-20 text-right">{item.size !== undefined ? formatBytes(item.size) : ''}</span>
              <span className="w-32 text-right">{item.last_modified ? formatDate(item.last_modified) : ''}</span>
            </div>
          )}

          {/* Delete confirmation inline */}
          {isDeleting && (
            <div className="ml-auto flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <span className="text-xs text-red-400">Delete?</span>
              <button
                onClick={() => handleDeleteConfirmed([item])}
                className="text-xs bg-red-600 hover:bg-red-500 text-white px-2 py-0.5 rounded"
              >
                Yes
              </button>
              <button
                onClick={() => setDeleteConfirmFile(null)}
                className="text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 px-2 py-0.5 rounded"
              >
                No
              </button>
            </div>
          )}
        </div>

        {/* Expanded children */}
        {isExpanded && expandedDirs[item.path]?.map((child, ci) =>
          renderRow(child, ci, depth + 1)
        )}
      </React.Fragment>
    );
  };

  // --- Breadcrumbs ---

  const pathParts = currentPath.split('/').filter(Boolean);
  const breadcrumbs = pathParts.map((part, i) => ({
    label: part,
    path: pathParts.slice(0, i + 1).join('/'),
  }));

  // --- Render ---

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800 bg-gray-900/50">
        <div className="flex items-center gap-2">
          {/* Back button */}
          <button
            onClick={navigateBack}
            disabled={pathHistory.length === 0}
            className="p-1.5 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded hover:bg-gray-800"
            title="Go back"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Breadcrumbs */}
          <div className="flex items-center text-sm text-gray-400 gap-1">
            <button
              onClick={navigateToRoot}
              className="hover:text-white transition-colors"
            >
              samples
            </button>
            {breadcrumbs.slice(1).map((bc) => (
              <React.Fragment key={bc.path}>
                <span className="text-gray-600">/</span>
                <button
                  onClick={() => navigateTo(bc.path)}
                  className="hover:text-white transition-colors"
                >
                  {bc.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* New folder */}
          <button
            onClick={() => setShowNewFolderDialog(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Folder
          </button>

          {/* Upload */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-500 rounded transition-colors disabled:opacity-50"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = e.target.files;
              if (files) {
                Array.from(files).forEach(f => handleUpload(f));
              }
              e.target.value = '';
            }}
          />

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-gray-800"
            title="Refresh"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Delete selected */}
          {selectedFiles.size > 0 && (
            <button
              onClick={() => {
                const selected = Array.from(selectedFiles).map(p => findItem(p)).filter(Boolean) as StorageItem[];
                if (selected.length > 0) handleDeleteConfirmed(selected);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-red-400 hover:text-red-300 bg-red-900/30 hover:bg-red-900/50 rounded transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete ({selectedFiles.size})
            </button>
          )}
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <div className="px-4 py-2 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <svg className="w-4 h-4 animate-spin text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <div className="flex-1">
              <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
            <span className="text-xs text-gray-400">{Math.round(uploadProgress)}%</span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-2 border-b border-gray-800 bg-red-900/20">
          <div className="flex items-center justify-between">
            <span className="text-sm text-red-400">{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* New folder dialog */}
      {showNewFolderDialog && (
        <div className="px-4 py-3 border-b border-gray-800 bg-gray-800/50">
          <div className="flex items-center gap-2">
            <FolderClosedIcon />
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="flex-1 bg-gray-900 text-white border border-gray-700 rounded px-3 py-1.5 text-sm outline-none focus:border-blue-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder();
                if (e.key === 'Escape') { setShowNewFolderDialog(false); setNewFolderName(''); }
              }}
            />
            <button
              onClick={handleCreateFolder}
              className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded"
            >
              Create
            </button>
            <button
              onClick={() => { setShowNewFolderDialog(false); setNewFolderName(''); }}
              className="px-3 py-1.5 text-xs bg-gray-700 hover:bg-gray-600 text-gray-300 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* File list */}
      <div
        className={`max-h-[600px] overflow-y-auto transition-colors ${containerDropActive ? 'bg-blue-900/10 ring-1 ring-inset ring-blue-500/30' : ''}`}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
            setContainerDropActive(true);
          }
        }}
        onDragLeave={(e) => {
          const related = e.relatedTarget as HTMLElement | null;
          if (related && (e.currentTarget as HTMLElement).contains(related)) return;
          setContainerDropActive(false);
        }}
        onDrop={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          setContainerDropActive(false);
          setDropTargetDir(null);
          if (e.dataTransfer.files.length > 0) {
            setUploading(true);
            setUploadProgress(0);
            setError(null);
            try {
              const files = Array.from(e.dataTransfer.files);
              for (const file of files) {
                await uploadFileToDir(file, currentPath);
              }
              onSyncNeeded?.();
              await handleRefresh();
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Upload failed');
            } finally {
              setUploading(false);
              setUploadProgress(0);
            }
          }
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <svg className="w-6 h-6 animate-spin text-gray-500" fill="none" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" opacity="0.25"/>
              <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="ml-3 text-gray-500 text-sm">Loading...</span>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p className="text-sm">This folder is empty</p>
            <p className="text-xs mt-1">Drop files here or use the Upload button</p>
          </div>
        ) : (
          items.map((item, index) => renderRow(item, index))
        )}
      </div>

      {/* Context menu */}
      {contextMenu && ReactDOM.createPortal(
        <div
          className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl py-1 z-50 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {contextMenu.item.type === 'file' && (
            <button
              onClick={() => { handleDownload(contextMenu.item); setContextMenu(null); }}
              className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
            >
              Download
            </button>
          )}
          <button
            onClick={() => startRename(contextMenu.item)}
            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            Rename
          </button>
          <button
            onClick={() => handleCopyPath(contextMenu.item)}
            className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white"
          >
            Copy Instance Path
          </button>
          <div className="border-t border-gray-700 my-1" />
          <button
            onClick={() => { setDeleteConfirmFile(contextMenu.item.path); setContextMenu(null); }}
            className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-900/30 hover:text-red-300"
          >
            Delete
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

export default AdminSampleExplorer;
